import React from 'react';
import { useTranslation } from 'react-i18next';
import { FinanceStats } from '../../../types/financeTypes';
import SkeletonLoader from '../../../components/Common/SkeletonLoader';
import '../../../components/Common/styles/skeleton.css';
import { formatMoney } from '../../../helpers/numberHelper';
import StatCard from '../../../components/Common/StatCard';
import { useAbility } from 'config/abilityContext';

interface FinanceStatsPanelProps {
  stats: FinanceStats | null;
  loading: boolean;
}

const FinanceStatsPanel: React.FC<FinanceStatsPanelProps> = ({ stats, loading }) => {
  const { t } = useTranslation();
  const ability = useAbility();

  if (loading) {
    return (
      <div className="row">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-comman w-100 skeleton-list-item">
              <div className="card-body">
                <div className="db-widgets d-flex justify-content-between align-items-center">
                  <div className="db-info">
                    <SkeletonLoader
                      type="text"
                      width="80px"
                      height="20px"
                      className="mb-2"
                      index={i}
                    />
                    <SkeletonLoader
                      type="text"
                      width="50px"
                      height="30px"
                      variant="large"
                      index={i}
                    />
                  </div>
                  <div className="db-icon">
                    <SkeletonLoader
                      type="circle"
                      width="40px"
                      height="40px"
                      index={i}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    {
      color: 'green' as const,
      icon: 'cash',
      title: t('total_fees'),
      hidden: !ability.can("getAll", "Fee"),
      value: formatMoney(stats?.totalIncome ?? 0, 'MAD'),
    },
    {
      color: 'blue' as const,
      icon: 'receipt',
      title: t('total_expenses'),
      hidden: !ability.can("getAll", "Expense"),
      value: formatMoney(stats?.totalExpense ?? 0, 'MAD'),
    },
    {
      color: 'red' as const,
      icon: 'clock',
      title: t('unpaid_fee'),
      hidden: !ability.can("getAll", "Fee"),
      value: formatMoney(stats?.pendingPayments ?? 0, 'MAD'),
    },
    {
      color: 'yellow' as const,
      icon: 'chart-bar',
      title: t('unpaid_expense'),
      hidden: !ability.can("getAll", "Expense"),
      value: formatMoney(stats?.recentTransactions ?? 0, 'MAD'),
    },
  ];

  return (
    <div className="row">
      {cards.map((card, index) => (
        !card.hidden && (
        <div key={index} className="col-xl-3 col-sm-6 col-12 d-flex">
          <StatCard
            color={card.color}
            icon={card.icon}
            title={card.title}
            value={card.value}
          />
        </div>
        )
      ))}
    </div>
  );
};

export default FinanceStatsPanel;
